import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { shippingApi } from "../../shared/shippingApi";
import {
  downloadPackingSlipPdf,
  getPackingSlipPdf,
  PackingSlipApiError,
} from "../../shared/packingSlipApi";
import SelectMenu from "../../components/controls/SelectMenu";
import type { Shipment, ShipmentStatus } from "../../types/shipping";
import type { SelectOption } from "../../types/api";

const quantityText = (value: number) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
const statusLabels: Record<ShipmentStatus, string> = {
  1: "In progress",
  4: "Shipped",
  5: "Ready for pickup",
  6: "Completed",
  7: "Void",
};
const statusTransitions: Record<ShipmentStatus, ShipmentStatus[]> = {
  1: [4, 5, 6, 7],
  4: [5, 6, 7],
  5: [4, 6, 7],
  6: [7],
  7: [],
};
const postedStatuses: ShipmentStatus[] = [4, 5, 6];

export default function ShippingDetails() {
  const { shippingId } = useParams();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [values, setValues] = useState<Record<number, string>>({});
  const [details, setDetails] = useState({ invoice_number: "", seal_number: "", notes: "" });
  const [selectedStatus, setSelectedStatus] = useState<ShipmentStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const load = async () => {
    if (!shippingId) return;
    setLoading(true);
    const result = await shippingApi.shipment(shippingId);
    if (result.success) {
      setShipment(result.data);
      setValues(
        Object.fromEntries(
          (result.data.items || []).map((item) => [item.id, String(item.quantity)])
        )
      );
      setDetails({
        invoice_number: result.data.invoice_number || "",
        seal_number: result.data.seal_number || "",
        notes: result.data.notes || "",
      });
      setSelectedStatus(result.data.status);
    } else setError(result.data?.message || "Could not load shipment.");
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, [shippingId]); // eslint-disable-line react-hooks/exhaustive-deps
  const print = async () => {
    if (!shippingId) return;
    try {
      const { blob, filename } = await getPackingSlipPdf(shippingId);
      downloadPackingSlipPdf(blob, filename);
    } catch (error: unknown) {
      setError(
        error instanceof PackingSlipApiError
          ? error.message
          : "Could not generate the packing slip PDF."
      );
    }
  };
  const invoice = async () => {
    if (!shipment) return;
    const result = await shippingApi.createInvoice(shipment.order_id);
    if (result.success) setMessage("Invoice created for this order.");
    else setError(result.data?.message || "Could not create invoice.");
  };
  const save = async () => {
    if (!shipment || !shippingId || selectedStatus === null) return;
    if (
      Number(shipment.status) === 1 &&
      postedStatuses.includes(selectedStatus) &&
      !window.confirm(
        `Mark this shipment ${statusLabels[selectedStatus].toLowerCase()} and post its quantities to the order?`
      )
    )
      return;
    if (
      selectedStatus === 7 &&
      Number(shipment.status) !== 7 &&
      !window.confirm("Void this shipment? Posted quantities will be reversed from the order.")
    )
      return;
    setSaving(true);
    setError("");
    setMessage("");
    const metadataResult = await shippingApi.updateShipment(shippingId, {
      invoice_number: details.invoice_number.trim() || null,
      seal_number: details.seal_number.trim() || null,
      notes: details.notes.trim() || null,
    });
    if (!metadataResult.success) {
      setError(metadataResult.data?.message || "Could not update shipment details.");
      setSaving(false);
      return;
    }
    if (Number(shipment.status) !== 7) {
      for (const item of shipment.items) {
        const quantity = Number(values[item.id]);
        if (!(quantity > 0)) {
          setError("Every shipped quantity must be greater than zero.");
          setSaving(false);
          return;
        }
        if (quantity !== Number(item.quantity)) {
          const itemResult = await shippingApi.correctItem(shippingId, item.id, quantity);
          if (!itemResult.success) {
            setError(
              itemResult.data?.message ||
                `Could not update ${item.product_name || "a shipment item"}.`
            );
            setSaving(false);
            return;
          }
        }
      }
    }
    if (selectedStatus !== Number(shipment.status)) {
      const statusResult = await shippingApi.updateStatus(shippingId, selectedStatus);
      if (!statusResult.success) {
        setError(
          statusResult.data?.message ||
            "Shipment information saved, but its status could not be updated."
        );
        setSaving(false);
        await load();
        return;
      }
    }
    setMessage("Shipment saved.");
    setSaving(false);
    await load();
  };
  if (loading && !shipment)
    return <p className="p-10 text-center text-gray-500">Loading shipment…</p>;
  if (!shipment)
    return (
      <p className="m-6 rounded-lg bg-red-50 p-4 text-red-700">{error || "Shipment not found."}</p>
    );
  const currentStatus = shipment.status;
  const isVoid = currentStatus === 7;
  const canCorrect = !isVoid;
  const statusOptions = [currentStatus, ...(statusTransitions[currentStatus] || [])].map(
    (value): SelectOption<ShipmentStatus> => ({ value, label: statusLabels[value] })
  );
  return (
    <div className="p-6 space-y-5">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Outbound record
            </p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">Shipment #{shipment.id}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {shipment.customer} ·{" "}
              {shipment.po_number ? `PO ${shipment.po_number}` : `Order #${shipment.order_id}`}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${isVoid ? "bg-red-100 text-red-700" : currentStatus === 4 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}
              >
                {statusLabels[currentStatus] || "Unknown"}
              </span>
              {shipment.invoice_number && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                  Invoice {shipment.invoice_number}
                </span>
              )}
              {shipment.seal_number && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                  Seal {shipment.seal_number}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:items-end">
            <SelectMenu
              parentClass="w-52 text-left"
              title="Status"
              name="status"
              options={statusOptions}
              value={statusOptions.find((option) => option.value === selectedStatus)}
              handleInputChange={(event) =>
                setSelectedStatus(event.target.value?.value ?? currentStatus)
              }
              disabled={isVoid}
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={save}
                disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <Link
                to={`/orders/${shipment.order_id}`}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                View order
              </Link>
              <button
                onClick={invoice}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Create invoice
              </button>
              <button
                onClick={print}
                className="rounded-lg bg-main-purple px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-600"
              >
                Print packing slip
              </button>
            </div>
          </div>
        </div>
      </section>
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
      )}
      {message && (
        <p className="rounded-lg bg-green-50 p-3 text-sm font-medium text-green-700">{message}</p>
      )}
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div>
          <h2 className="font-semibold text-gray-900">Shipment information</h2>
          <p className="text-sm text-gray-500">
            Update the fields, quantities, or status, then select Save.
          </p>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            Invoice number
            <input
              value={details.invoice_number}
              onChange={(event) => setDetails({ ...details, invoice_number: event.target.value })}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Seal number
            <input
              value={details.seal_number}
              onChange={(event) => setDetails({ ...details, seal_number: event.target.value })}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="text-sm font-medium text-gray-700 md:col-span-2">
            Notes
            <textarea
              value={details.notes}
              onChange={(event) => setDetails({ ...details, notes: event.target.value })}
              rows={3}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
        </div>
      </section>
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900">Shipped items</h2>
          <p className="text-sm text-gray-500">
            {canCorrect
              ? "Edit quantities, then select Save."
              : "Void shipment quantities are locked because their inventory effect has already been reversed."}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Product
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Barcode
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Shipped quantity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shipment.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-gray-900">{item.product_name || "Product"}</p>
                    <p className="text-xs text-gray-500">SKU {item.sku || "—"}</p>
                  </td>
                  <td className="px-5 py-4 font-mono text-sm text-gray-500">
                    {item.shipping_barcode?.startsWith("manual-")
                      ? "Manual entry"
                      : item.shipping_barcode || "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {!canCorrect ? (
                      <span className="font-mono tabular-nums">{quantityText(item.quantity)}</span>
                    ) : (
                      <input
                        type="number"
                        min="0.0001"
                        step="0.0001"
                        value={values[item.id] || ""}
                        onChange={(e) => setValues({ ...values, [item.id]: e.target.value })}
                        className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-right font-mono tabular-nums"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
